import { initialCombobox } from "./state";

import type { Combobox, Item, State } from "./state";

/**
 * The combobox with id `comboboxId` if it exists, otherwise `undefined`.
 */
export const combobox = (
  state: State,
  comboboxId: Combobox["comboboxId"]
): Combobox | undefined => {
  return state.comboboxes[comboboxId];
};

/**
 * The combobox with id `comboboxId` if it exists, otherwise the initial state for
 * a combobox.
 */
export const comboboxOrInitial = (
  state: State,
  comboboxId: Combobox["comboboxId"]
): Combobox | typeof initialCombobox => {
  return state.comboboxes[comboboxId] ?? initialCombobox;
};

/**
 *  Group portals in the correct order for rendering.
 *
 * - Default: [].
 */
export const groupPortals = (state: State, comboboxId: Combobox["comboboxId"]) => {
  return comboboxOrInitial(state, comboboxId).groupPortals;
};

/**
 * The value of the highlighted item.
 *
 * - Default: "".
 */
export const highlightedValue = (state: State, comboboxId: Combobox["comboboxId"]) => {
  return comboboxOrInitial(state, comboboxId).highlightedValue;
};

/** Whether `value` is the highlighted value. */
export const isHighlightedValue = (
  state: State,
  comboboxId: Combobox["comboboxId"],
  value: Combobox["selectedValue"]
) => {
  return comboboxOrInitial(state, comboboxId).highlightedValue === value;
};

/**
 * Whether the select listbox is open.
 *
 * - Default: false.
 */
export const isOpen = (state: State, comboboxId: Combobox["comboboxId"]) => {
  return comboboxOrInitial(state, comboboxId).isOpen;
};

/** Whether `value` is the selected value. */
export const isSelected = (
  state: State,
  comboboxId: Combobox["comboboxId"],
  value: Combobox["selectedValue"]
) => {
  return comboboxOrInitial(state, comboboxId).selectedValue === value;
};

/**
 * The values of filtered and sorted items in render order.
 *
 * - Default: [].
 */
export const itemOrder = (state: State, comboboxId: Combobox["comboboxId"]) => {
  return comboboxOrInitial(state, comboboxId).itemOrder;
};

/**
 *  Group portals in render order.
 *
 * - Default: [].
 */
export const itemPortals = (
  state: State,
  comboboxId: Combobox["comboboxId"],
  groupId: Item["groupId"]
) => {
  return comboboxOrInitial(state, comboboxId).itemPortals.get(groupId);
};

/**
 * Whether to keep the listbox open.
 *
 * - Default: false.
 */
export const keepOpen = (state: State, comboboxId: Combobox["comboboxId"]) => {
  return comboboxOrInitial(state, comboboxId).keepOpen;
};

/**
 * Whether filter needs to be called to update the results.
 *
 * - Default: false.
 */
export const needsFilter = (state: State, comboboxId: Combobox["comboboxId"]) => {
  return comboboxOrInitial(state, comboboxId).needsFilter;
};

/**
 * The selected item display text.
 *
 * - Default: "".
 */
export const selectedText = (state: State, comboboxId: Combobox["comboboxId"]) => {
  const combobox = comboboxOrInitial(state, comboboxId);
  const text = combobox.items.get(combobox.selectedValue)?.text ?? "";

  return typeof text === "string" ? text : text.display;
};

/**
 * The selected item value.
 *
 * - Default: "".
 */
export const selectedValue = (state: State, comboboxId: Combobox["comboboxId"]) => {
  return comboboxOrInitial(state, comboboxId).selectedValue;
};
